import { useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'

import { cn } from '@/lib/utils'
import { MaskedName } from '@/lib/privacyMask'
import type { SoundCheckItem } from '@/types'
import '@/styles/placement-flow.css'

interface ProbeAScreenProps {
  item: SoundCheckItem
  learnerName: string
  onResult: (correct: boolean) => void
  /** Cumulative items answered this placement; drives the accent rotation. */
  doneCount?: number
  submitting?: boolean
}

const TAP_FEEDBACK_MS = 150
const IDLE_PULSE_MS = 10_000
const ACCENT_IS_LIGHT = [true, false, false, false]

type Verdict = 'yes' | 'not-yet' | null

export function ProbeAScreen({
  item,
  learnerName,
  onResult,
  doneCount = 0,
  submitting = false,
}: ProbeAScreenProps) {
  const [verdict, setVerdict] = useState<Verdict>(null)
  const [pulse, setPulse] = useState(false)
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const accentIdx = doneCount % ACCENT_IS_LIGHT.length
  const accentTone = ACCENT_IS_LIGHT[accentIdx] ? 'light' : 'dark'

  useEffect(() => {
    if (verdict !== null) return
    idleTimerRef.current = setTimeout(() => setPulse(true), IDLE_PULSE_MS)
    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
    }
  }, [verdict, item.word])

  const pick = (next: Verdict) => {
    if (verdict !== null || submitting) return
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
    setPulse(false)
    setVerdict(next)
    setTimeout(() => onResult(next === 'yes'), TAP_FEEDBACK_MS)
  }

  return (
    <motion.div
      key={item.word}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0, scale: pulse ? [1, 1.02, 1] : 1 }}
      transition={{
        opacity: { duration: 0.25 },
        y: { duration: 0.25 },
        scale: pulse
          ? { duration: 1.4, repeat: Infinity, ease: 'easeInOut' }
          : { duration: 0.15 },
      }}
      className="probe-a-screen"
    >
      <div className="probe-a-screen__say">
        Say this word out loud to <MaskedName>{learnerName}</MaskedName>
      </div>

      <span className="probe-a-screen__word">
        <span
          aria-hidden="true"
          className={`probe-a-screen__swipe is-accent-${accentIdx} is-${accentTone}`}
        />
        <span className="probe-a-screen__word-text">{item.word}</span>
      </span>

      <div className="probe-a-screen__ask">
        <span className="probe-a-screen__ask-label">Then ask:</span>
        <p className="probe-a-screen__ask-copy">
          &ldquo;What sound does it start with?&rdquo;
        </p>
      </div>

      <p className="probe-a-screen__fallback">
        If <MaskedName>{learnerName}</MaskedName> isn&rsquo;t here, just think about whether they could do this.
      </p>

      <div className="mt-10 flex w-full max-w-md justify-center gap-4 pa-actions">
        <SoundButton
          disabled={verdict !== null || submitting}
          active={verdict === 'yes'}
          onClick={() => pick('yes')}
          icon={<SpeakerIcon />}
          label={`They say "${item.target_sound}"`}
        />
        <SoundButton
          disabled={verdict !== null || submitting}
          active={verdict === 'not-yet'}
          onClick={() => pick('not-yet')}
          label="Not yet"
        />
      </div>
    </motion.div>
  )
}

interface SoundButtonProps {
  disabled: boolean
  active: boolean
  onClick: () => void
  label: string
  icon?: React.ReactNode
}

function SoundButton({ disabled, active, onClick, label, icon }: SoundButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={cn(
        'hs-press hs-press--paper pa-action-btn probe-a-screen__button',
        disabled && !active && 'opacity-50',
        active && 'is-active',
      )}
    >
      {icon}
      <span>{label}</span>
    </button>
  )
}

function SpeakerIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--ink)"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M7 11l4-7c1.5 0 2.5 1 2.5 2.5L13 9h5.5c1 0 1.7 1 1.4 2l-1.8 6.5c-.2.9-1 1.5-1.9 1.5H7M7 11v9M7 11H4v9h3" />
    </svg>
  )
}
